import { observer } from 'mobx-react-lite';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import { setSmartPillBlockVisibility } from '../actions/smartPillBlockActions';
import createSmartPillArbitrator from '../arbitrators/createSmartPillArbitrator';
import registerSmartPillBuilders from '../builders/registerSmartPillBuilders';
import registerSmartPillLogDataUpdaters from '../logDataUpdaters/registerSmartPillLogDataUpdaters';
import registerSmartPillPreRunners from '../preRunners/registerSmartPillPreRunners';
import getFeedbackData from '../utils/getFeedbackData';
import initializeSIGSData from 'owa-mail-smart-pill-features/lib/utils/initializeSIGSData';
import { logToSIGSOnRender } from 'owa-mail-smart-pill-features/lib/utils/logToSIGS';
import { SmartPillViewState } from 'owa-mail-reading-pane-store';
import { ClientItem } from 'owa-mail-store';
import { SmartPillBlock } from 'owa-smart-pill-ui';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { findInlineComposeViewState } from 'owa-mail-compose-actions';
import { trace } from 'owa-trace';
import * as React from 'react';
import {
    ArbitratedSmartPills,
    getPillsToRender,
    SmartPillArbitrator,
    SmartPillBuilder,
    SmartPillLogDataUpdater,
    SmartPillPreRunner,
} from 'owa-smart-pill';

const styles = require('./MailSmartPillBlock.scss');
const PILL_TIMEOUT: number = 3000; // Based on SmartPillError logs, timeout of 3000 means 99%+ of executions finish in time.

export interface MailSmartPillBlockProps {
    item: ClientItem;
    viewState: SmartPillViewState;
}

/**
 * The component handles the rendering of the SmartPillBlock in owa mail.
 * Responsibilities:
 *  - Register all smart pill builders used by OWA
 *  - Register smart pill arbitrators and chose which arbitrator to run
 *  - Run all applicable smart pill builders and cache results on the component
 */
export default observer(function MailSmartPillBlock(props: MailSmartPillBlockProps) {
    React.useEffect(() => {
        // Item is loaded, run the smart pill builders.
        runSmartPillBuilders();
        return () => {
            smartPillBuilders.current = null;
            smartPillArbitrator.current = null;
            smartPillData.current = null;
            smartPillPreRunners.current = null;
            // For item part collapse, we need to set the visibility to false.
            // Otherwise re-render won't occur when item part is expanded.
            setSmartPillBlockVisibility(props.viewState, false);
        };
    }, []);

    React.useEffect(() => {
        // Log to SIGS only once to prevent double logging.
        if (!loggedToSIGS.current) {
            loggedToSIGS.current = logToSIGSOnRender(props.item);
        }
    });

    const smartPillBuilders = React.useRef<SmartPillBuilder[]>(
        registerSmartPillBuilders(props.item)
    );
    const smartPillArbitrator = React.useRef<SmartPillArbitrator>(createSmartPillArbitrator());
    const smartPillLogDataUpdaters = React.useRef<SmartPillLogDataUpdater[]>(
        registerSmartPillLogDataUpdaters(props.item)
    );
    const smartPillPreRunners = React.useRef<SmartPillPreRunner[]>(
        registerSmartPillPreRunners(props.item)
    );
    const smartPillData = React.useRef<ArbitratedSmartPills>();
    const loggedToSIGS = React.useRef<boolean>(false);
    const isComposeFormOpened = useComputed((): boolean => {
        const { item } = props;
        if (item?.ItemId?.Id) {
            return findInlineComposeViewState(item.ItemId.Id, ReactListViewType.Message) != null;
        }
        return false;
    });
    const runSmartPillBuilders = async () => {
        trace.info('[MailSmartPillBlock] Running smart pill builders');
        smartPillData.current = await getPillsToRender(
            smartPillBuilders.current,
            smartPillArbitrator.current,
            smartPillPreRunners.current,
            smartPillLogDataUpdaters.current,
            PILL_TIMEOUT
        );
        // Initialize data for SIGS
        // This method must be executed AFTER we know which pills to show/hide, i.e. smartPillData is non-null.
        initializeSIGSData(props.item, smartPillData.current);
        if (smartPillData.current && smartPillData.current.pillsToShow.length > 0) {
            // Show the smart bill bar
            // Even if pillsToShow is empty, we still need to set this to true
            // Otherwise we can't log data about hidden pills
            setSmartPillBlockVisibility(props.viewState, true);
        }
    };
    const { item, viewState } = props;
    const { smartPillBlockVisible } = viewState;
    // If the item has IRM protections, the RightsManagementLicenseData property will be non-null.
    const isIRMMessage: boolean = !!item.RightsManagementLicenseData;
    // We do not render anything if:
    // 1) The message has IRM protections, OR
    // 2) SmartPillData is undefined, OR
    // 3) smartPillBlockVisible is false, OR,
    // 4) There are no pills to show AND no pills to hide, OR
    // 5) The compose form is already opened
    if (
        isIRMMessage ||
        !smartPillBlockVisible ||
        !smartPillData.current ||
        (smartPillData.current.pillsToShow.length == 0 &&
            smartPillData.current.pillsToHide.length == 0) ||
        isComposeFormOpened.get()
    ) {
        return null;
    }
    const pillsWillBeShown: boolean = smartPillData.current.pillsToShow.length > 0;
    // To avoid the RP jumping when we aren't actually rendering pills,
    // don't apply styling unless we are actually rendering pills. However, we
    // must always render the SmartPillBlock for counterfactual logging.
    return (
        <div className={pillsWillBeShown ? styles.bar : undefined}>
            <SmartPillBlock
                arbitratedPills={smartPillData.current}
                enableFeedbackForm={!item.smartPillFeedbackSubmitted}
                feedbackData={getFeedbackData(smartPillData.current.pillsToShow, item)}
            />
        </div>
    );
});
